import React, { useState, useEffect, useRef, useCallback } from "react";
import { LazyEditorWrapper } from "./styles";
import { REQUEST_IDLE_CALLBACK_TIMEOUT } from "constants/AppConstants";
import type {
  EditorProps,
  EditorStyleProps,
} from "components/editorComponents/CodeEditor";
import type CodeEditor from "components/editorComponents/CodeEditor";
import CodeEditorFallback from "./CodeEditorFallback";
import { CODE_EDITOR_LOADING_ERROR } from "ee/constants/messages";
import assertNever from "assert-never/index";
import log from "loglevel";
import { toast } from "@appsmith/ads";
import { SearchOverlay } from "./SearchOverlay";

let CachedCodeEditor: typeof CodeEditor | undefined;

type LazyCodeEditorState =
  // The initial state when the state machine is initialized
  | "idle"
  // The state when the CodeEditor chunk is loading
  | "loading"
  // The state when the CodeEditor chunk is loading, but the user has interacted with the placeholder
  | "loading-interacted"
  // The state when we’re waiting for the idle callback to fire to render CodeEditor
  | "waiting-idle-callback"
  // The state when the CodeEditor must be rendered
  | "active"
  // The state when the CodeEditor must be rendered and made focused
  | "active-focused"
  // The state when the CodeEditor chunk failed to load
  | "error";

type LazyCodeEditorEvent =
  | "RENDERED"
  | "LOADING_FINISHED"
  | "LOADING_ERRORED"
  | "IDLE_CALLBACK_FIRED"
  | "PLACEHOLDER_INTERACTED";

/**
 * A state machine that manages the lifecycle of the CodeEditor chunk.
 * It’s implemented outside the component to avoid firing unnecessary re-renders
 * when the state changes.
 */
class LazyCodeEditorStateMachine {
  private state: LazyCodeEditorState;
  private idleCallbackId: number | null = null;
  private stateChangeListeners: ((state: LazyCodeEditorState) => void)[] = [];

  constructor(stateChangeListener: (state: LazyCodeEditorState) => void) {
    this.state = "idle";
    void this.handleStateChange();
    this.stateChangeListeners.push(stateChangeListener);
  }

  /** This method transitions the state machine to a new state based on the current state
   * and (possibly) other conditions. It’s intended to not have any side effects –
   * all side effects should go into this.handleStateChange() */
  transition(event: LazyCodeEditorEvent) {
    switch (this.state) {
      case "idle":
        if (event === "RENDERED") {
          if (CachedCodeEditor) {
            this.state = "waiting-idle-callback";
          } else {
            this.state = "loading";
          }
        }

        break;
      case "loading":
        if (event === "LOADING_FINISHED") {
          this.state = "waiting-idle-callback";
        } else if (event === "PLACEHOLDER_INTERACTED") {
          this.state = "loading-interacted";
        }

        break;
      case "loading-interacted":
        if (event === "LOADING_FINISHED") {
          this.state = "active-focused";
        }

        break;
      case "waiting-idle-callback":
        if (event === "IDLE_CALLBACK_FIRED") {
          this.state = "active";
        }

        if (event === "PLACEHOLDER_INTERACTED") {
          this.state = "active-focused";
        }

        break;
      case "active":
        break;
      case "active-focused":
        break;
      case "error":
        break;
      default:
        assertNever(this.state);
    }

    void this.handleStateChange();
  }

  /** This method runs side effects when a state transition happens. */
  private async handleStateChange() {
    this.stateChangeListeners.forEach((listener) => listener(this.state));

    switch (this.state) {
      case "idle":
        break;

      case "loading":
        try {
          const codeEditorModule = await import(
            "components/editorComponents/CodeEditor"
          );

          // Once CodeEditor loads, save it, so we can render it synchronously in the future
          CachedCodeEditor = codeEditorModule.default;
          this.transition("LOADING_FINISHED");
        } catch (error) {
          log.error(error);
          // TODO: Fix this the next time the file is edited
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          toast.show(CODE_EDITOR_LOADING_ERROR((error as any).message), {
            kind: "error",
          });
          this.transition("LOADING_ERRORED");
        }
        break;

      case "loading-interacted":
        break;

      case "waiting-idle-callback":
        this.idleCallbackId = window.requestIdleCallback(
          () => {
            this.transition("IDLE_CALLBACK_FIRED");
          },
          { timeout: REQUEST_IDLE_CALLBACK_TIMEOUT.highPriority },
        );
        break;

      case "active":
      case "active-focused":
      case "error":
        if (this.idleCallbackId) {
          window.cancelIdleCallback(this.idleCallbackId);
        }

        break;

      default:
        assertNever(this.state);
    }
  }
}

/**
 * A wrapper to load and lazily render the CodeEditor component on demand.
 *
 * - Why load on demand: CodeEditor is a large chunk of the bundle, so we want to load it only when needed.
 *
 * - Why lazily render: CodeMirror is a performance intensive component to render.
 *     Many widgets have multiple properties that require a CodeEditor component to be rendered.
 *     Solution:
 *     1. Lazy load the CodeEditor component when the system is idle.
 *     2. Render a similar looking replica component initially.
 *     3. If there isn't enough idle time to render the CodeEditor component,
 *        then render it immediately upon focus event.
 */
function LazyCodeEditor({
  input,
  onLoad,
  placeholder,
  ...otherProps
}: EditorProps &
  EditorStyleProps & {
    onLoad?: () => void;
  }) {
  const [renderTarget, setRenderTarget] = useState<
    "editor" | "editor-focused" | "fallback"
  >("fallback");
  const [showLoadingProgress, setShowLoadingProgress] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const lastSearchQueryRef = useRef<string>("");
  const cmRef = useRef<any>(null);
  const wrapperDivRef = useRef<HTMLDivElement | null>(null);
  const currentCursor = useRef<any>(null);
  const [totalMatches, setTotalMatches] = useState<number>(0);
  const [currentMatch, setCurrentMatch] = useState<number>(0);

  const stateMachine = useRef(
    new LazyCodeEditorStateMachine((state) => {
      if (state === "active") setRenderTarget("editor");
      if (state === "active-focused") setRenderTarget("editor-focused");
      if (state === "loading-interacted") setShowLoadingProgress(true);
    })
  );

  const ensureEditorReady = useCallback(() => {
    if (cmRef.current) return true;

    const wrapper = wrapperDivRef.current;
    if (!wrapper) return false;

    const maybe = wrapper.querySelector(
      ".cm-editor, .CodeMirror, .cm-content, .CodeMirrorWrapper"
    ) as any;
    if (!maybe) return false;

    const possibleKeys = [
      "view",
      "editorView",
      "CodeMirror",
      "editor",
      "cm",
    ];
    for (const k of possibleKeys) {
      if (maybe[k]) {
        cmRef.current = maybe[k];
        break;
      }
    }
    if (!cmRef.current) cmRef.current = maybe;
    return !!cmRef.current;
  }, []);

  const editorWrapperRef = useCallback(
    (wrapper: HTMLDivElement | null) => {
      wrapperDivRef.current = wrapper;
      if (wrapper && renderTarget === "editor-focused") {
        const target = wrapper.querySelector(
          ".CodeEditorTarget"
        ) as HTMLElement | null;
        target?.focus();
      }
    },
    [renderTarget]
  );

  useEffect(() => {
    stateMachine.current.transition("RENDERED");
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    if (renderTarget === "editor" || renderTarget === "editor-focused") {
      window.addEventListener("keydown", handler);
      return () => window.removeEventListener("keydown", handler);
    }
  }, [renderTarget]);

  useEffect(() => {
    if (
      (renderTarget === "editor" || renderTarget === "editor-focused") &&
      CachedCodeEditor
    ) {
      onLoad?.();
    }
  }, [renderTarget, CachedCodeEditor, onLoad]);

  useEffect(() => {
    if (renderTarget === "editor" || renderTarget === "editor-focused") {
      const timer = setTimeout(() => ensureEditorReady(), 0);
      return () => clearTimeout(timer);
    }
  }, [renderTarget, ensureEditorReady]);

  const countAllMatches = (query: string): number => {
    const cursor = getCursor(query);
    if (!cursor) return 0;

    let count = 0;
    while (cursor.findNext()) {
      count += 1;
    }
    return count;
  };

  const offsetToPos = (doc: string, offset: number, isCM6: boolean) => {
    const lines = doc.slice(0, offset).split("\n");
    const line = lines.length - 1;
    const ch = lines[lines.length - 1].length;
    return isCM6 ? { offset } : { line, ch };
  };

  function makeSimpleCursor(
    doc: string,
    query: string,
    startIdx = 0,
  ) {
    const lowerDoc = doc.toLowerCase();
    const lowerQuery = query.toLowerCase();
    let forwardIdx = lowerDoc.indexOf(lowerQuery, startIdx);
    let backwardIdx = lowerDoc.lastIndexOf(lowerQuery, startIdx - 1);

    return {
      findNext(this: any) {
        if (forwardIdx === -1) return false;
        this.from = forwardIdx;
        this.to = forwardIdx + query.length;
        forwardIdx = lowerDoc.indexOf(lowerQuery, this.to);
        backwardIdx = lowerDoc.lastIndexOf(lowerQuery, this.from - 1);

        return true;
      },
      findPrevious(this: any) {
        if (backwardIdx === -1) return false;
        this.from = backwardIdx;
        this.to = backwardIdx + query.length;
        forwardIdx = lowerDoc.indexOf(lowerQuery, this.to);
        backwardIdx = lowerDoc.lastIndexOf(lowerQuery, this.from - 1);

        return true;
      },
      from: -1,
      to: -1,
    };
  }

  const getCursor = (query: string) => {
    const cm = cmRef.current;
    if (!cm) return null;
    if (cm.getValue) {
      const doc = cm.getValue();
      return makeSimpleCursor(doc, query);
    }
    const doc = cm.state.doc.toString();
    return makeSimpleCursor(doc, query);
  };

  const moveTo = (cursor: any, forward: boolean) => {
    const cm = cmRef.current;
    if (!cm) return false;
    const moved = forward ? cursor.findNext() : cursor.findPrevious();
    if (!moved) return false;

    const doc = cm.getValue ? cm.getValue() : cm.state.doc.toString();
    const isCM6 = !!cm.dispatch;

    const startPos = offsetToPos(doc, cursor.from, isCM6);
    const endPos = offsetToPos(doc, cursor.to, isCM6);

    if (cm.setSelection) {
      cm.setSelection(startPos, endPos, { scroll: true });
    } else if (cm.dispatch) {

      const anchor = forward ? cursor.from : cursor.to;
      const head = forward ? cursor.to : cursor.from;

      cm.dispatch({
        selection: { anchor, head },
        scrollIntoView: true,
      });
    }

    return true;
  };

  const performSearch = (query: string) => {
    if (lastSearchQueryRef.current !== query) {
      const total = countAllMatches(query);
      setTotalMatches(total);
      setCurrentMatch(total > 0 ? 1 : 0);
    }

    if (lastSearchQueryRef.current === query && currentCursor.current) {
      const moved = goToNext();
      if (!moved) {
        resetToFirstMatch();
      }
      return;
    }

    lastSearchQueryRef.current = query;

    const cursor = getCursor(query);
    if (!cursor) {
      toast.show(`No match for "${query}"`, { kind: "info" });
      return;
    }

    if (!moveTo(cursor, true)) {
      toast.show(`No match for "${query}"`, { kind: "info" });
      return;
    }
    currentCursor.current = cursor;
    setCurrentMatch(1);
  };

  const resetToFirstMatch = () => {
    const query = lastSearchQueryRef.current;
    if (!query) return false;
    const cursor = getCursor(query);
    if (!cursor) return false;
    const moved = moveTo(cursor, true);
    if (moved) {
      currentCursor.current = cursor;
      setCurrentMatch(1);
    }
    return moved;
  };

  const goToNext = () => {
    if (currentCursor.current) {
      const moved = moveTo(currentCursor.current, true);
      if (moved) {
        setCurrentMatch(prev => {
          const next = prev + 1;
          return next > totalMatches ? 1 : next;
        });
      } else {
        const isMoved = resetToFirstMatch();
        if (!isMoved) {
          toast.show(`No more matches found`, { kind: "info" });
          return
        }
        return isMoved;
      }
      return moved
    }
    return false;
  };

  const goToPrev = () => {
    if (!currentCursor.current) return false;

    const moved = moveTo(currentCursor.current, false);
    if (moved) {
      setCurrentMatch(prev => {
        const next = prev - 1;
        return next < 1 ? totalMatches : next;
      });
    } else {
      toast.show(`No previous match`, { kind: "info" });
    }
    return moved;
  };

  if (renderTarget === "editor" || renderTarget === "editor-focused") {
    if (!CachedCodeEditor) {
      throw new Error(
        "CodeEditor is not loaded. This is likely an issue with the state machine.",
      );
    }

    return (
      <LazyEditorWrapper
        className="t--lazyCodeEditor-editor"
        ref={editorWrapperRef}
        style={{ position: "relative" }}
      >

        <CachedCodeEditor
          input={input}
          placeholder={placeholder}
          {...otherProps}
        />

        {showSearch && (
          <SearchOverlay
            onSearch={performSearch}
            onClose={() => {
              setShowSearch(false)
              setCurrentMatch(0)
              setTotalMatches(0)
              lastSearchQueryRef.current = ""
            }}
            onNext={goToNext}
            onPrev={goToPrev}
            totalMatches={totalMatches}
            currentMatch={currentMatch}
          />
        )}
      </LazyEditorWrapper>

    );
  }

  if (renderTarget === "fallback") {
    return (
      <LazyEditorWrapper className="t--lazyCodeEditor-fallback">
        <CodeEditorFallback
          borderLess={otherProps.borderLess}
          height={otherProps?.height}
          input={input}
          isReadOnly={otherProps.isReadOnly}
          onInteracted={() => {
            stateMachine.current.transition("PLACEHOLDER_INTERACTED");
          }}
          placeholder={placeholder}
          showLineNumbers={otherProps.showLineNumbers}
          showLoadingProgress={showLoadingProgress}
        />
      </LazyEditorWrapper>
    );
  }

  throw new Error(`Invalid render target: ${renderTarget}`);
}

export default LazyCodeEditor;